import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Checkbox
} from '@mui/material';

import {
  Search,
  FilterList,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Refresh,
  Clear,
  Store,
  Assignment,
  Verified,
  CalendarToday,
  ThumbUp,
  ThumbUpAlt,
  History
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import Sidebar from '../components/sidebars/Sidebar _inventory';
import TopNav from '../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  maxWidth: '100%',
  '& .MuiTable-root': {
    minWidth: 800,
    width: 'max-content',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transition: 'background-color 0.2s ease',
  }
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const AuditorApproval = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [auditorName, setAuditorName] = useState('');

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Selection states for bulk approval
  const [selectedSupplies, setSelectedSupplies] = useState([]);
  
  // Supplies states
  const [pendingSupplies, setPendingSupplies] = useState([]);
  const [approvedSupplies, setApprovedSupplies] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Fetch pending supplies
  const fetchPendingSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_pending_supplies.php?${params}`);
      if (response.data.success) {
        setPendingSupplies(response.data.data);
        setTotalPending(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching pending supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch pending supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch approved supplies
  const fetchApprovedSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_approved_supplies.php?${params}`);
      if (response.data.success) {
        setApprovedSupplies(response.data.data);
        setTotalApproved(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching approved supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch approved supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory/get_categories.php`);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      fetchPendingSupplies();
    } else {
      fetchApprovedSupplies();
    }
    fetchCategories();
  }, [activeTab, fetchPendingSupplies, fetchApprovedSupplies, fetchCategories]);

  useEffect(() => {
    // Clear selection when tab changes
    setSelectedSupplies([]);
  }, [activeTab]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    
    if (userName) {
      setAuditorName(userName);
    } else if (userEmail) {
      setAuditorName(userEmail);
    } else {
      setAuditorName('Auditor');
    }
  }, []);

  // Approve single supply
  const handleApproveSupply = async () => {
    setLoading(true);
    try {
      const payload = {
        id: selectedSupply.id,
        approved_by: auditorName
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/approve_supply.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `"${selectedSupply.item_name}" approved successfully`,
          severity: 'success'
        });
        handleCloseApproveDialog();
        fetchPendingSupplies();
        fetchApprovedSupplies();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error approving supply:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to approve supply',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Approve multiple supplies
  const handleBulkApprove = async () => {
    if (selectedSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one supply to approve',
        severity: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ids: selectedSupplies,
        approved_by: auditorName
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/approve_multiple_supplies.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        setSelectedSupplies([]);
        setBulkApproveDialogOpen(false);
        fetchPendingSupplies();
        fetchApprovedSupplies();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error bulk approving supplies:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to approve supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApproveDialog = (supply) => {
    setSelectedSupply(supply);
    setApproveDialogOpen(true);
  };

  const handleCloseApproveDialog = () => {
    setApproveDialogOpen(false);
    setSelectedSupply(null);
  };

  const handleOpenBulkApproveDialog = () => {
    if (selectedSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one supply to approve',
        severity: 'warning'
      });
      return;
    }
    setBulkApproveDialogOpen(true);
  };

  const handleCloseBulkApproveDialog = () => {
    setBulkApproveDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange({ start: '', end: '' });
    setSelectedSupplies([]);
  };

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange({ start: '', end: '' });
    handleFilterClose();
  };

  // Handle select all/none
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = pendingSupplies.map((supply) => supply.id);
      setSelectedSupplies(newSelected);
    } else {
      setSelectedSupplies([]);
    }
  };

  // Handle single select
  const handleSelect = (id) => {
    const selectedIndex = selectedSupplies.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedSupplies, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedSupplies.slice(1));
    } else if (selectedIndex === selectedSupplies.length - 1) {
      newSelected = newSelected.concat(selectedSupplies.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedSupplies.slice(0, selectedIndex),
        selectedSupplies.slice(selectedIndex + 1),
      );
    }

    setSelectedSupplies(newSelected);
  };

  // Sort data
  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_received' || orderBy === 'total_invoice_value') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'date_received') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getCurrentData = () => {
    const data = activeTab === 0 ? pendingSupplies : approvedSupplies;
    return sortData(data);
  };

  const filteredData = getCurrentData();
  const totalItems = activeTab === 0 ? totalPending : totalApproved;

  // Statistics
  const statistics = useMemo(() => {
    const pendingCount = totalPending;
    const approvedCount = totalApproved;
    const selectedCount = selectedSupplies.length;
    
    return { pendingCount, approvedCount, selectedCount };
  }, [totalPending, totalApproved, selectedSupplies]);

  // Render Pending Supplies Table (with approve button)
  const renderPendingTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ width: 50 }}>
              <Checkbox
                indeterminate={selectedSupplies.length > 0 && selectedSupplies.length < pendingSupplies.length}
                checked={pendingSupplies.length > 0 && selectedSupplies.length === pendingSupplies.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Quantity
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Requested By</TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No pending supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={item.id} hover selected={selectedSupplies.indexOf(item.id) !== -1}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedSupplies.indexOf(item.id) !== -1}
                    onChange={() => handleSelect(item.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                      <Pending sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{item.unit_of_measurement}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_received).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.date_received}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={`Requested by: ${item.added_by_staffid}`}>
                    <Chip 
                      label={item.added_by_staffid || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <GlowingButton
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<ThumbUp />}
                    onClick={() => handleOpenApproveDialog(item)}
                    sx={{ minWidth: 100 }}
                  >
                    Approve
                  </GlowingButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Approved Supplies Table (view only)
  const renderApprovedTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Quantity
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Requested By</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Approved By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Approval Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No approved supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                      <Verified sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{item.unit_of_measurement}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_received).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.date_received}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.added_by_staffid || 'Unknown'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={item.checked_by || 'Auditor'} 
                    size="small" 
                    color="success"
                    variant="outlined"
                    icon={<Verified sx={{ fontSize: 14 }} />}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  const renderCurrentTable = () => {
    if (activeTab === 0) return renderPendingTable();
    return renderApprovedTable();
  };

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh', overflowX: 'hidden' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%',
        overflowX: 'hidden'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />
       
        <Box sx={{ mt: 3, width: '100%', overflowX: 'hidden' }}>
          <Paper elevation={0} sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', width: '100%' }}>
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="h5" fontWeight={700}>
                Auditor Approval Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Welcome, {auditorName}. Review and approve supply requests.
              </Typography>
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                }
              }}
            >
              <Tab icon={<Pending />} iconPosition="start" label={`Pending (${statistics.pendingCount})`} />
              <Tab icon={<History />} iconPosition="start" label={`Approved History (${statistics.approvedCount})`} />
            </Tabs>

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.01) }}>
              <Grid item xs={12} sm={activeTab === 0 ? 6 : 12}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {activeTab === 0 ? 'Pending Approvals' : 'Total Approved'}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1} color={activeTab === 0 ? 'warning.main' : 'success.main'}>
                          {activeTab === 0 ? statistics.pendingCount : statistics.approvedCount}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(activeTab === 0 ? theme.palette.warning.main : theme.palette.success.main, 0.1), 
                                    color: activeTab === 0 ? theme.palette.warning.main : theme.palette.success.main }}>
                        {activeTab === 0 ? <Pending /> : <Verified />}
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              {activeTab === 0 && (
                <Grid item xs={12} sm={6}>
                  <StyledCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Selected for Approval
                          </Typography>
                          <Typography variant="h4" fontWeight={700} mt={1} color="primary.main">
                            {statistics.selectedCount}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                          <ThumbUpAlt />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              )}
            </Grid>

            {/* Search and Filters Bar */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by item name, category, or requester..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <GlowingButton
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={handleFilterClick}
                    >
                      Filters
                    </GlowingButton>
                    
                    {activeTab === 0 && (
                      <GlowingButton
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUpAlt />}
                        onClick={handleOpenBulkApproveDialog}
                        disabled={selectedSupplies.length === 0}
                      >
                        Bulk Approve ({selectedSupplies.length})
                      </GlowingButton>
                    )}
                    
                    <GlowingButton
                      variant="outlined"
                      startIcon={<Download />}
                    >
                      Export
                    </GlowingButton>
                    
                    <IconButton onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setDateRange({ start: '', end: '' });
                      if (activeTab === 0) {
                        fetchPendingSupplies();
                      } else {
                        fetchApprovedSupplies();
                      }
                    }}>
                      <Refresh />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Active Filters Display */}
            {(selectedCategory !== 'all' || dateRange.start) && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Active Filters:</Typography>
                {selectedCategory !== 'all' && (
                  <Chip label={`Category: ${selectedCategory}`} onDelete={() => setSelectedCategory('all')} size="small" />
                )}
                {dateRange.start && (
                  <Chip label={`Date: ${dateRange.start} to ${dateRange.end}`} onDelete={() => setDateRange({ start: '', end: '' })} size="small" />
                )}
                <Button size="small" onClick={clearFilters} sx={{ ml: 1 }}>Clear All</Button>
              </Box>
            )}

            {/* Table */}
            {renderCurrentTable()}
          </Paper>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, width: 320, borderRadius: '16px' } }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Filters</Typography>
        
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Category</InputLabel>
          <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
          value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
          value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
          sx={{ mb: 2 }}
        />
        
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters} size="small">Clear All</Button>
          <Button variant="contained" onClick={handleFilterClose} size="small">Apply</Button>
        </Stack>
      </Popover>

      {/* Single Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog}>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve <strong>"{selectedSupply?.item_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Quantity: {selectedSupply?.quantity_received} {selectedSupply?.unit_of_measurement}<br />
            Total Value: ₦{Number(selectedSupply?.total_invoice_value).toLocaleString()}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will mark the supply as approved and it will be added to the inventory.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveSupply}
            disabled={loading}
            startIcon={<ThumbUp />}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approve Confirmation Dialog */}
      <Dialog open={bulkApproveDialogOpen} onClose={handleCloseBulkApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve <strong>{selectedSupplies.length} supply(s)</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will approve all selected supplies and add them to the inventory.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>Selected Items:</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
              {pendingSupplies
                .filter(s => selectedSupplies.includes(s.id))
                .map(s => (
                  <Typography key={s.id} variant="body2" sx={{ py: 0.5 }}>
                    • {s.item_name} ({s.quantity_received} {s.unit_of_measurement})
                  </Typography>
                ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkApprove}
            disabled={loading}
            startIcon={<ThumbUpAlt />}
          >
            {loading ? <CircularProgress size={24} /> : `Approve All (${selectedSupplies.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditorApproval;